/*
 * The way out of a send nobody could confirm, offered only once LeadCue has stopped looking for it
 * (`docs/2026-10-22-forward-release-decisions.md`, Change A). "It went out" settles it as sent.
 * "It didn't go out" takes a second, separate press: LeadCue cannot see what the person found in
 * Bison, and a wrong answer lets the lead receive the same message twice.
 */
import { useState } from "react";
import { appDateTime } from "../../src/app-dates.ts";
import type { SendCheck, SendSettlementRequest } from "../../src/inbox-v2/contract.ts";

export type Settle = (
  action: SendSettlementRequest["action"],
  candidateAt?: string,
) => Promise<void> | undefined;

export function SettleSend(props: {
  check: SendCheck | undefined;
  kind: "reply" | "forward";
  busy: boolean;
  onSettle: Settle;
}) {
  const { check, kind, busy } = props;
  const [confirming, setConfirming] = useState(false);
  if (!check?.stopped) return null;
  if (confirming)
    return (
      <fieldset className="lcr-i2-settle" aria-label={`This ${kind} did not go out`}>
        <p className="lcr-send-line">
          Check Bison's Sent folder first. If this {kind} went out and you send again, it arrives
          twice.
        </p>
        <div className="lcr-i2-uncertain-actions">
          <button
            type="button"
            className="lcr-small-button"
            disabled={busy}
            onClick={() => void props.onSettle("not_sent")}
          >
            I checked: it isn't there
          </button>
          <button
            type="button"
            className="lcr-ghost lcr-small-button"
            disabled={busy}
            onClick={() => setConfirming(false)}
          >
            Cancel
          </button>
        </div>
      </fieldset>
    );
  const ambiguous = check.stopped === "ambiguous";
  return (
    <fieldset className="lcr-i2-settle lcr-i2-uncertain-actions" aria-label={`Settle this ${kind}`}>
      {ambiguous ? (
        check.candidates.map((at) => (
          <button
            key={at}
            type="button"
            className="lcr-small-button"
            disabled={busy}
            onClick={() => void props.onSettle("went_out", at)}
          >
            It went out at {appDateTime(at)}
          </button>
        ))
      ) : (
        <button
          type="button"
          className="lcr-small-button"
          disabled={busy}
          onClick={() => void props.onSettle("went_out")}
        >
          It went out
        </button>
      )}
      <button
        type="button"
        className="lcr-ghost lcr-small-button"
        disabled={busy}
        onClick={() => setConfirming(true)}
      >
        {ambiguous ? "Neither is this one" : "It didn't go out"}
      </button>
    </fieldset>
  );
}
